import api from '../config/api';

/**
 * Support Issues Service
 * Handles all support issue and comments related API calls
 */

/**
 * Get all support issues with filters and pagination
 * @param {Object} params - Query parameters
 * @param {string} params.search
 * @param {string} params.issue_type
 * @param {string} params.priority
 * @param {string} params.status
 * @param {number|string} params.reported_by
 * @param {number|string} params.assigned_to
 * @param {number|string} params.customer_id
 * @param {number|string} params.agency_id
 * @param {number} params.limit
 * @param {number} params.offset
 * @param {string} params.order_by
 * @param {string} params.sort_order
 * @returns {Promise<Object>} API response
 */
export const getSupportIssues = async (params = {}) => {
  try {
    const {
      search,
      issue_type,
      priority,
      status,
      reported_by,
      assigned_to,
      customer_id,
      agency_id,
      limit = 10,
      offset = 0,
      order_by,
      sort_order,
    } = params;

    const queryParams = {
      limit,
      offset,
    };

    if (search) queryParams.search = search;
    if (issue_type) queryParams.issue_type = issue_type;
    if (priority) queryParams.priority = priority;
    if (status) queryParams.status = status;
    if (reported_by) queryParams.reported_by = reported_by;
    if (assigned_to) queryParams.assigned_to = assigned_to;
    if (customer_id) queryParams.customer_id = customer_id;
    if (agency_id) queryParams.agency_id = agency_id;
    if (order_by) queryParams.order_by = order_by;
    if (sort_order) queryParams.sort_order = sort_order;

    const response = await api.get('/api/support-issues', { params: queryParams });
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to fetch support issues. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get a single support issue by ID
 * @param {number|string} id
 * @param {boolean} include_comments
 * @returns {Promise<Object>}
 */
export const getSupportIssueById = async (id, include_comments = false) => {
  try {
    const response = await api.get(`/api/support-issues/${id}`, {
      params: { include_comments },
    });
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to fetch support issue. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create a new support issue
 * @param {Object} issueData
 * @returns {Promise<Object>}
 */
export const createSupportIssue = async (issueData) => {
  try {
    const response = await api.post('/api/support-issues', issueData);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to create support issue. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update an existing support issue
 * @param {number|string} id
 * @param {Object} issueData
 * @returns {Promise<Object>}
 */
export const updateSupportIssue = async (id, issueData) => {
  try {
    const response = await api.put(`/api/support-issues/${id}`, issueData);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to update support issue. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete a support issue
 * @param {number|string} id
 * @returns {Promise<Object>}
 */
export const deleteSupportIssue = async (id) => {
  try {
    const response = await api.delete(`/api/support-issues/${id}`);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to delete support issue. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Add a comment to a support issue
 * @param {number|string} issueId
 * @param {Object} commentData
 * @returns {Promise<Object>}
 */
export const addSupportIssueComment = async (issueId, commentData) => {
  try {
    const response = await api.post(`/api/support-issues/${issueId}/comments`, commentData);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to add comment. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete a comment from a support issue
 * @param {number|string} issueId
 * @param {number|string} commentId
 * @returns {Promise<Object>}
 */
export const deleteSupportIssueComment = async (issueId, commentId) => {
  try {
    const response = await api.delete(
      `/api/support-issues/${issueId}/comments/${commentId}`
    );
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to delete comment. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Support Issue Comments - Global listing APIs
 */

export const getSupportIssueComments = async (params = {}) => {
  try {
    const {
      issue_id,
      commented_by,
      is_internal,
      limit = 10,
      offset = 0,
      order_by,
      sort_order,
    } = params;

    const queryParams = {
      limit,
      offset,
    };

    if (issue_id) queryParams.issue_id = issue_id;
    if (commented_by) queryParams.commented_by = commented_by;
    if (typeof is_internal === 'boolean') queryParams.is_internal = is_internal;
    if (order_by) queryParams.order_by = order_by;
    if (sort_order) queryParams.sort_order = sort_order;

    const response = await api.get('/api/support-issue-comments', {
      params: queryParams,
    });
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to fetch support issue comments. Please try again.';
    throw new Error(errorMessage);
  }
};

export const getSupportIssueCommentById = async (id) => {
  try {
    const response = await api.get(`/api/support-issue-comments/${id}`);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to fetch comment. Please try again.';
    throw new Error(errorMessage);
  }
};

export const createSupportIssueComment = async (commentData) => {
  try {
    const response = await api.post('/api/support-issue-comments', commentData);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to create comment. Please try again.';
    throw new Error(errorMessage);
  }
};

export const updateSupportIssueComment = async (id, commentData) => {
  try {
    const response = await api.put(`/api/support-issue-comments/${id}`, commentData);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to update comment. Please try again.';
    throw new Error(errorMessage);
  }
};

export const deleteSupportIssueCommentById = async (id) => {
  try {
    const response = await api.delete(`/api/support-issue-comments/${id}`);
    return response.data || response;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      'Failed to delete comment. Please try again.';
    throw new Error(errorMessage);
  }
};


